<template>
  <el-dialog title="绑定飞书" :visible.sync="dialogVisible" width="420px"
             :close-on-click-modal="false" :close-on-press-escape="false" append-to-body>
    <div class="fs-bind-body">
      <p class="fs-bind-desc">
        检测到您还未绑定飞书账号。绑定后即可使用飞书免登、飞书消息通知等服务。
      </p>
      <div class="fs-bind-actions">
        <el-button type="primary" :loading="binding" @click="goAuthorize">去飞书授权绑定</el-button>
        <el-button :disabled="binding" @click="dialogVisible = false">暂不绑定</el-button>
      </div>
      <p class="fs-bind-tip">点击后将跳转到飞书授权页，请在打开的页面中完成授权后自动返回。</p>
    </div>
  </el-dialog>
</template>

<script>
import {getAuthUser, getToken} from "@/utils/auth";
import {getFeishuApp} from "@/api/common";
import {loginByFeishuCode, getAuth, bindFeishuToCurrentUser} from "@/api/login";
import {buildFeishuAuthorizeUrl, getFeishuRedirectUri} from "@/utils/feishu";

/**
 * 登录后检测未绑定飞书 → 弹窗引导飞书授权绑定
 * ① 已登录 ② 用户未绑定飞书 ③ 本次登录未提示过，三者同时满足时弹窗
 * 触发时机：created（刷新/直达已登录页面）与 $EventBus['bind-dd']（各类登录成功后）
 */
export default {
  name: "BindFeishu",
  data() {
    return {
      dialogVisible: false,
      binding: false
    }
  },
  mounted() {
    // this.init()
    // 授权回调（state=feishu&code）优先消费，避免被当作普通进入处理
    if (this.isFeishuCallback()) {
      this.handleFeishuCallback()
    } else {
      this.init()
    }
    // this.$EventBus && this.$EventBus.$on('bind-dd', this.init)
  },
  beforeDestroy() {
    // this.$EventBus && this.$EventBus.$off('bind-dd', this.init)
  },
  methods: {
    // 判断 URL 是否携带飞书授权回调参数
    isFeishuCallback() {
      return this.getQueryVariable('state') === 'feishu' && !!this.getQueryVariable('code')
    },
    getQueryVariable(variable) {
      const query = window.location.search.substring(1)
      if (!query) return false
      const vars = query.split('&')
      for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=')
        if (pair[0] === variable) return decodeURIComponent(pair[1] || '')
      }
      return false
    },
    // 从地址栏清除一次性 OAuth 授权参数，避免其他组件（如钉钉扫码）误读
    stripOAuthQuery() {
      const keys = ['code', 'state', 'authCode']
      const search = window.location.search
      if (!search) return
      const kept = search.substring(1).split('&').filter(item => {
        const k = item.split('=')[0]
        return keys.indexOf(k) === -1
      })
      const newSearch = kept.length ? ('?' + kept.join('&')) : ''
      window.history.replaceState(null, '', window.location.origin + window.location.pathname + newSearch + window.location.hash)
    },
    // 一次性授权码是否已消费过（防重复消费 → 飞书 20003）
    isCodeConsumed(code) {
      if (!code) return false
      return sessionStorage.getItem('feishu_used_code') === code
    },
    // 标记该授权码已消费
    consumeCode(code) {
      if (code) sessionStorage.setItem('feishu_used_code', code)
    },
    // 是否已绑定飞书
    isBound() {
      const user = getAuthUser()
      return !!(user && (user.fs_union_id || user.fs_open_id || user.fs_user_id))
    },
    init() {
      this.dialogVisible = false
      // ① 已登录
      if (!getToken()) return
      // ② 未绑定飞书
      if (this.isBound()) return
      // ③ 本次登录未提示过
      if (localStorage.getItem('showBindFeishu')) return
      this.dialogVisible = true
      localStorage.setItem('showBindFeishu', '1')
    },
    // 跳转飞书授权页绑定
    async goAuthorize() {
      if (this.binding) return
      try {
        const {model} = await getFeishuApp()
        if (!model || !model.app_id) {
          this.$message.error('飞书应用未配置，请联系管理员')
          return
        }
        this.binding = true
        // 安全配置的重定向 URL 指定为 /home，回调固定到 /home 以命中白名单
        window.location.href = buildFeishuAuthorizeUrl(model, getFeishuRedirectUri('/home'), 'feishu')
      } catch (e) {
        this.$message.error('获取飞书配置失败，请重试')
        this.binding = false
      }
    },
    // 处理飞书授权回调：已登录 → 绑定到当前账号；未登录 → 飞书登录
    async handleFeishuCallback() {
      if (getToken()) {
        await this.handleBindCurrentUser()
      } else {
        await this.handleFeishuLogin()
      }
    },
    // 已登录：将飞书身份绑定到当前账号
    async handleBindCurrentUser() {
      if (this.isBound()) {
        // this.$message.info('您已绑定飞书')
        this.stripOAuthQuery()
        return
      }
      const code = this.getQueryVariable('code')
      // 一次性授权码去重：同一 code 只向后端消费一次，防止回调被多个消费者重复使用
      if (this.isCodeConsumed(code)) {
        this.stripOAuthQuery()
        return
      }
      this.consumeCode(code)
      this.stripOAuthQuery()
      this.binding = true
      try {
        const res = await bindFeishuToCurrentUser({code})
        if (res.success) {
          localStorage.setItem('showBindFeishu', '1')
          const meRes = await getAuth()
          const me = meRes.model || {}
          this.$store.dispatch('login', me)
          this.$message.success('飞书绑定成功')
        } else {
          this.$message.error((res.model && res.model.message) || res.message || '飞书绑定失败，请重试')
        }
      } catch (e) {
        this.$message.error('飞书绑定失败，请重试')
      } finally {
        this.binding = false
        // 稍作等待后重新判断（绑定成功一般已不再弹窗）
        setTimeout(() => this.init(), 300)
      }
    },
    // 未登录：走飞书授权登录（匹配账号/首次注册）
    async handleFeishuLogin() {
      const code = this.getQueryVariable('code')
      // 一次性授权码去重：同一 code 只向后端消费一次，防止回调被多个消费者重复使用
      if (this.isCodeConsumed(code)) {
        this.stripOAuthQuery()
        return
      }
      this.consumeCode(code)
      this.stripOAuthQuery()
      this.binding = true
      try {
        const res = await loginByFeishuCode(code)
        if (res.success && res.model && res.model.is_register) {
          // 飞书未绑定系统账号 → 携带飞书临时信息前往绑定页（注册/绑定已有账号）
          localStorage.setItem('userFeishu', JSON.stringify(res.model.user_info || {}))
          this.$router.push({path: '/bindFeishu'})
          return
        }
        if (res.success && res.model && !res.model.is_register) {
          // 飞书登录成功：更新 token 并重新拉取用户信息（含飞书绑定字段）
          this.$utils.lsSet('token', res.model.access_token, res.model.expires_in * 1000)
          localStorage.setItem('showBindFeishu', '1')
          const meRes = await getAuth()
          const me = meRes.model || {}
          this.$store.dispatch('login', me)
          // this.$message.success('飞书登录成功')
          // 登录成功后跳转到原重定向页（本次飞书登录发起时保存），无则回默认首页 /
          const redirect = sessionStorage.getItem('feishu_redirect')
          sessionStorage.removeItem('feishu_redirect')
          this.$router.push(redirect || '/')
        } else {
          this.$message.error((res.model && res.model.message) || res.message || '飞书登录失败：该飞书账号未匹配到系统账号')
        }
      } catch (e) {
        this.$message.error('飞书登录失败，请重试')
      } finally {
        this.binding = false
        // 稍作等待后重新判断（绑定成功一般已不再弹窗）
        setTimeout(() => this.init(), 300)
      }
    }
  }
}
</script>

<style scoped>
.fs-bind-body {
  text-align: center;
  padding: 8px 4px 4px;
}

.fs-bind-desc {
  font-size: 14px;
  color: #606266;
  line-height: 22px;
  margin: 0 0 20px;
}

.fs-bind-actions {
  margin-bottom: 14px;
}

.fs-bind-tip {
  font-size: 12px;
  color: #909399;
  margin: 0;
}
</style>
